import { History } from "history";
import * as _ from "lodash";
import { inject, observer } from "mobx-react";
import React, { Component, createRef, forwardRef, MouseEvent, useEffect, useImperativeHandle, Fragment, useMemo, useState } from "react";
import { Badge, Button, Col, Dropdown, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from "react-bootstrap";
import { NavLink, NavigateFunction, Location, useNavigate, useLocation, useParams } from "react-router-dom";
import { DeviceType, TableauEventType, TableauViz, Toolbar } from "@tableau/embedding-api";

import { ColumnDef, flexRender, getCoreRowModel, getExpandedRowModel, Row as RowInfo, ColumnSort, useReactTable, getPaginationRowModel, ColumnFiltersState, getFilteredRowModel, SortingState, getSortedRowModel, ExpandedState, Updater, type Table as ITable } from "@tanstack/react-table";
import Auth from "../../common/auth/auth";
import { Defaults, Utils } from "@/common/utils";
import { isWithinRangeFilter, isActionPlanStatusFilter, isColumnNumberFilter } from "@/common/filters";
import { AppStore, AttachmentStore, CommentStore, FeatureGatingStore, MilestoneStore, ReportStore, SnapshotStore, StrategicPlanStore, TrustedTokenStore } from "@/stores";
import { type StrategicPlanItem } from "Stores/strategicPlanStore";
import { Dashboard } from "Stores/dashboardStore";
import { MilestoneStatus } from "Stores/milestoneStore";

import { Ticket, TicketStore } from "Stores/dashboardStore/tickets";

import { TicketDetailForm } from "./DetailForm";
import { D3Dashboard } from "./D3Actionplans";
import { SnapshotConfirmModal, ActionplanCloseModal } from "./ActionPlanModals";
import CONFIG from "Config";
import { useQuery } from "@/hooks/router";
import SearchActionPlans from "./SearchActionPlans";
import PagePagination from "./PagePagination";
import FilterButton from "./FilterMenu";
import { format } from "date-fns";
import TableMenu from "./TableMenu";
import { ArrowDown, ArrowDownUp, ArrowUp, EllipsisVertical, Eye, Plus, Upload, X } from "lucide-react";
import { ToastContext } from "@/contexts/toastContext";
import { useActionPlans, useActionPlanById } from "@/hooks/actionplans";
import { ActionPlanSearchFilters } from "../Reports/TicketSearchForm";
import { getStatusLabelFromApi } from "@/common/effectivenessActionPlanDrillDown";
import { when } from "mobx";

enum VIEW_OPTIONS {
    DEFAULT,
    STRATEGIC,
}

export interface ListProps {
    appStore?: AppStore;
    ticketStore?: TicketStore;
    reportStore?: ReportStore;
    strategicPlanStore?: StrategicPlanStore;
    trustedTokenStore?: TrustedTokenStore;
    dashboard?: Dashboard;
    instaOpenId?: string;
    overdue?: boolean;
    dashboardId?: number;
    refreshView?: string | null;
    history?: History;
    location?: Location;
    triggerSnapshot?: () => void;
    isReporting?: boolean;
    tickets?: Ticket[];
    selectTicket?: (row: RowInfo<Ticket>) => void;
    selectAllTickets?: (select: boolean, tickets: Ticket[] | undefined) => void;
    generateReport?: () => void;
    onNewSearch?: (fn: () => void) => void;
    pageSize?: number;
    isReadyToAddComment?: boolean;
    formRef?: any;
    hasToExpandActionPlan?: boolean;
    navigate?: NavigateFunction;
    featureGatingStore?: FeatureGatingStore;
    showQuickActionPlan?: boolean;
    attachmentStore?: AttachmentStore;
    snapshotStore?: SnapshotStore;
    milestoneStore?: MilestoneStore;
    commentStore?: CommentStore;
    searchFilters?: ActionPlanSearchFilters;
}

interface ListState {
    showFilters: boolean;
    showMore: boolean;
    instaOpenId: string;
    dashboardId?: number;
    fetchOnUpdateDashboard: boolean;
    pageSize: number;
    pageNumber: number;
    expand?: { [index: number]: boolean };
    initialLoadDone: boolean;
    didFetchAll: boolean;
    numOfTix: number;
    allTicketsSelected?: boolean;
    deletingActionPlan: undefined | string;
    showQuickActionPlan: boolean;
    view: VIEW_OPTIONS;
    refreshKey?: string | null;
    sortOptions: ColumnSort[];
    autoOpenIndex: number;
    openDropdownIndex: string | null;
    showSnapshotConfirm: boolean;
    showCloseModal: boolean;
    currentClosingTicket?: Ticket;
    isClosingActionPlan: boolean;
    closeError?: string;
    showActionPlanSuccess?: boolean;
}

const StatusMap = {
    open: "Open",
    closed: "Closed",
    "goal-met": "Achieved",
    "goal-not-met": "Unmet",
};

@inject("appStore", "ticketStore", "reportStore", "strategicPlanStore", "trustedTokenStore", "featureGatingStore", "attachmentStore", "snapshotStore", "milestoneStore", "commentStore")
@observer
export class TicketList extends Component<ListProps, ListState> {
    static contextType = ToastContext;
    declare context: React.ContextType<typeof ToastContext>;
    _isMounted = false;

    autoOpenIndex?: number;
    defaultPage = Defaults.tablePage();
    defaultPageSize = this.props.pageSize ? this.props.pageSize : Defaults.tablePageSize();
    columns: ColumnDef<Ticket>[];
    quickActionPlanRef;
    container: HTMLDivElement | null;
    auth?: Auth;
    listener: any;
    vizDidLoad: boolean;
    vizIsComplete: boolean;
    viz?: TableauViz;

    constructor(props: ListProps) {
        super(props);
        const params = new URLSearchParams(location.search);
        const pageNumber = params.get("page");
        const pageSize = params.get("page_size");
        this.state = {
            showMore: false,
            showFilters: true,
            expand: undefined,
            instaOpenId: this.props.instaOpenId ? this.props.instaOpenId : "",
            // get page number from query params or set defaults page
            pageNumber: pageNumber ? Number(pageNumber) : this.defaultPage,
            pageSize: pageSize ? Number(pageSize) : this.defaultPageSize,
            initialLoadDone: false,
            didFetchAll: false,
            numOfTix: this.props.tickets ? this.props.tickets.length : 0,
            allTicketsSelected: !!this.props.tickets,
            deletingActionPlan: undefined,
            showQuickActionPlan: !!props.showQuickActionPlan,
            view: VIEW_OPTIONS.DEFAULT,
            sortOptions: [],
            fetchOnUpdateDashboard: false,
            dashboardId: 0,
            refreshKey: this.props.refreshView,
            autoOpenIndex: -1,
            openDropdownIndex: null,
            showSnapshotConfirm: false,
            showCloseModal: false,
            currentClosingTicket: undefined,
            isClosingActionPlan: false,
            closeError: undefined,
            showActionPlanSuccess: false,
        };

        if (this.props.onNewSearch) {
            this.props.onNewSearch(this.resetPage);
        }

        const view = this.getParams().get("view");
        if (view && view.toUpperCase() === "SP") {
            this.state = { ...this.state, view: VIEW_OPTIONS.STRATEGIC, sortOptions: [{ id: "strategic_plan_name", desc: false }] };
        }

        this.columns = this.getColumns();
    }

    filterableColumns = [
        { key: "assignee_name", label: "Assignee" },
        { key: "created_by_name", label: "Created By" },
        { key: "dashboard", label: "Dashboard name" },
        { key: "name", label: "Title" },
        { key: "collaborator_name", label: "Collaborator Name" },
        { key: "student", label: "Student Name" },
        { key: "course", label: "Course Name" },
        { key: "committees", label: "Committee Group" },
        { key: "labels", label: "Labels" },
    ];

    // Read fresh on every render: FilterButton and SearchActionPlans rebuild the URL
    // from these params, so a snapshot taken at construction would push the first
    // URL's query (e.g. ?savedViewId) onto every dashboard switched to afterwards (EAI-5733).
    get params() {
        return new URLSearchParams(window.location.search);
    }

    tableRef = createRef<ActionPlanTableHandle>();

    toggleDropdown = (rowIndex: string) => {
        this.setState((prevState) => ({
            openDropdownIndex: prevState.openDropdownIndex === rowIndex ? null : rowIndex,
        }));
    };

    ensureAuth() {
        if (this.vizIsComplete && !this.vizDidLoad && this.auth) {
            this.auth.loginRedirect();
        }
    }

    handleVizLoaded = () => {
        this.vizDidLoad = true;
    };

    static getDerivedStateFromProps(nextProps: ListProps, state) {
        let nextState: Partial<ListState> = {};
        if (!state || (nextProps.instaOpenId && nextProps.instaOpenId !== state.instaOpenId)) {
            nextState = { instaOpenId: nextProps.instaOpenId };
        }

        if (!state || (nextProps.dashboardId && nextProps.dashboardId !== state.dashboardId)) {
            nextState = { ...nextState, dashboardId: nextProps.dashboardId };
            if (state && state.initialLoadDone) {
                nextState = { ...nextState, fetchOnUpdateDashboard: true, initialLoadDone: false, didFetchAll: false, expand: {} };
            }
        }

        if (Object.keys(nextState).length !== 0) {
            return nextState;
        }

        return null;
    }

    componentDidMount() {
        if (!this.props.dashboardId) {
            const listener = (msg: any) => {
                if (msg.data && _.isString(msg.data) && msg.data.includes("tableau.completed")) {
                    this.vizIsComplete = true;
                    setTimeout(() => this.ensureAuth(), 2000);
                }
            };
            this.listener = listener;
            this.auth = new Auth();
            addEventListener("message", listener);
        }

        if (!this.props.isReporting && !this.props.dashboardId) {
            Utils.setViewName("TicketList", "ActionPlans®");
        }

        if (this.props.tickets) {
            this.setState({ initialLoadDone: true, didFetchAll: false });
        } else {
            this.fetchAppropriateTickets(undefined, true);
        }

        this._isMounted = true;
        if (this.props.strategicPlanStore) this.props.strategicPlanStore.fetchStrategicPlanItems(true);
        this.checkRenderAndInit();
        if (this.props.showQuickActionPlan) {
            setTimeout(() => {
                if (this.quickActionPlanRef !== undefined) {
                    this.quickActionPlanRef.scrollIntoView();
                }
            }, 1000);
        }

        const closeActionPlanRuuid = new URLSearchParams(window.location.search).get("closeActionPlan");
        if (closeActionPlanRuuid) {
            when(
                () => this.props.ticketStore !== undefined && !this.props.ticketStore.isFetchingTickets,
                () => {
                    const ticket = this.props.ticketStore!.tickets?.find((t) => t.ruuid === closeActionPlanRuuid);
                    if (ticket) {
                        this.setState({
                            currentClosingTicket: ticket,
                            showCloseModal: true,
                        });
                    }
                }
            );
        }
    }

    componentWillUnmount() {
        delete this.auth;
        this._isMounted = false;
        this.setState({ initialLoadDone: false, didFetchAll: false });
        if (this.viz) {
            this.viz.remove();
            this.viz.removeEventListener(TableauEventType.FirstInteractive, () => { });
            this.viz = undefined;
        }
    }

    async componentDidUpdate() {
        if (this.state.fetchOnUpdateDashboard) {
            this.setState({ fetchOnUpdateDashboard: false });
            if (this.state.dashboardId) {
                await this.fetchAppropriateTickets(this.state.dashboardId, true);
            }
        }
    }

    checkRenderAndInit = () => {
        if (!this.container) {
            setTimeout(this.checkRenderAndInit, 500);
        } else {
            this.initTableau();
        }
    };

    handleActionplanReload = () => {
        this.setState({ refreshKey: new Date().toISOString() });

        // Trigger SWR refetch for real-time updates
        if (this.tableRef.current) {
            this.tableRef.current.mutateActionPlans();
        }

        if (this.props.dashboardId) {
            this.props.ticketStore!.getTicketsForDashboard(this.props.dashboardId, this.state.pageSize, 0).then(() => {
                this.setState({ expand: this.getInitialExpandedRow() });
            });
        } else {
            this.props.ticketStore!.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC).then(() => {
                this.setState({ expand: this.getInitialExpandedRow(), numOfTix: this.props.ticketStore!.tickets ? this.props.ticketStore!.tickets.length : 0 });
            });
        }
    };

    handleFiltersChange = (numOfTix: number) => {
        // Inner table calls this when SWR's total_count resolves. Don't reset pageNumber
        // here — that races with handleAutoOpen's pagination jump on deep-link mount and
        // also clobbers the page the user is currently on whenever the count refreshes.
        this.setState({ numOfTix });
    };

    pushHistory = (url, params?) => {
        if (!this.props.navigate) return;
        this.props.navigate(`${url}?${params ? params : this.getParams()}`);
    };

    getParams = () => {
        const location = this.props.location;
        return new URLSearchParams(location ? location.search : location);
    };

    handleClearDrillDownFilter = () => {
        if (!this.props.navigate || !this.props.location) return;
        const params = new URLSearchParams(this.props.location.search);
        params.delete("strategic_plan_ruuid");
        params.delete("strategic_plan_name");
        params.delete("status");
        const search = params.toString();
        this.props.navigate({ pathname: this.props.location.pathname, search });
    };

    handleSelectAllToggle = () => {
        if (this.props.selectAllTickets) {
            const table = this.tableRef.current?.getTableInstance();
            const rows = table?.getRowModel().rows;
            const tix = rows ? rows.map(row => row.original) : (this.props.tickets || !this.props.ticketStore ? [] : this.props.ticketStore.tickets);
            const shouldSelectAll = this.props.tickets ? false : this.props.ticketStore && this.props.ticketStore.allSelected !== undefined ? !this.props.ticketStore.allSelected : !this.state.allTicketsSelected;
            this.props.selectAllTickets(shouldSelectAll, tix);
        }
        this.setState({ allTicketsSelected: !this.state.allTicketsSelected });
        // Recalculate this.columns to force a rerender of react-table to update the header checkbox
        this.columns = this.getColumns();
    };

    getStrategicPlanItems = (items: StrategicPlanItem[]) => items.map((item) => item.name).join(", ");

    isActionPlanSelected = (row: Ticket) => {
        if (this.props.reportStore?.selectedTickets) {
            return this.props.reportStore.selectedTickets.find(ticket => ticket.ruuid === row.ruuid) !== undefined;
        }
        return false;
    };

    getColumns = () => {
        const columns: ColumnDef<Ticket>[] = [];

        columns.push(
            {
                header: "ID",
                accessorKey: "display_id",
                size: 50,
                filterFn: isColumnNumberFilter,
            },
            {
                header: "Created on",
                accessorKey: "created",
                cell: (props) => format(new Date(props.row.original.created), "MMM d, yyyy"),
                filterFn: isWithinRangeFilter,
                size: 150,
            },
            {
                header: "Due Date",
                accessorKey: "evaluation_date",
                filterFn: isWithinRangeFilter,
                cell: (props) => {
                    if (props.row.original.type === "observation" && props.row.original.evaluation_date === "") return "N/A";
                    if (props.row.original.evaluation_date === "") return "";
                    if (typeof props.row.original.evaluation_date !== "string") return "";
                    return format(Utils.parseISOLocalDate(props.row.original.evaluation_date), "MMM d, yyyy");
                },
                size: 100,
            },
            {
                header: "Status",
                accessorKey: "status",
                cell: (props) => {
                    const status = props.row.original.status;
                    const isTargetMet = props.row.original.target_met;

                    // If closed, show goal-met or goal-not-met status
                    if (status === "closed") {
                        return (
                            <div className="badge-container">
                                <Badge bg="light" className={`status-${isTargetMet ? "goal-met" : "goal-not-met"}`}>
                                    {StatusMap[isTargetMet ? "goal-met" : "goal-not-met"]}
                                </Badge>
                            </div>
                        );
                    }

                    // If open, show open status
                    if (status === "open") {
                        return (
                            <div className="badge-container">
                                <Badge bg="light" className="status-open">
                                    {StatusMap["open"]}
                                </Badge>
                            </div>
                        );
                    }

                    // For observations and tasks without due dates (status is null/empty)
                    return "N/A";
                },
                filterFn: isActionPlanStatusFilter,
                minSize: 95,
                maxSize: 100,
            }
        );

        if (this.state.view === VIEW_OPTIONS.STRATEGIC) {
            columns.push({
                id: "strategic_plan_items",
                header: "Institutional Effectiveness Plan",
                accessorKey: "strategic_plan_items",
                cell: ({ row }) => {
                    return row.original.strategic_plan_items.length ? this.getStrategicPlanItems(row.original.strategic_plan_items) : "-";
                },
                size: 200,
            });
        } else {
            columns.push({
                header: "Dashboard Name",
                accessorKey: "dashboard",
                cell: (props) => Utils.getDisplayDashboardName(props.row.original.dashboard),
                size: 150,
            });
        }

        columns.push(
            {
                header: "Title",
                accessorKey: "name",
                cell: (props) => {
                    // Older ActionPlans stored `name` truncated to 30 chars; the full
                    // title lives in `dashboard_interpretation` (EAI-5685).
                    const fullTitle = props.row.original.dashboard_interpretation || props.row.original.name || "";
                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={`actionplan-title-tooltip-${props.row.original.ruuid}`}>
                                    {fullTitle}
                                </Tooltip>
                            }
                        >
                            <span className="actionplan-title">{fullTitle}</span>
                        </OverlayTrigger>
                    );
                },
                size: 350,
            },
            {
                header: "Author",
                accessorKey: "created_by_name",
                cell: (props) => {
                    return props.row.original.created_by_name ? props.row.original.created_by_name : "";
                },
                size: 125,
            },
            {
                header: "Assignee",
                id: "assignee_name",
                accessorKey: "assignee_name",
                cell: (props) => {
                    return props.row.original.assignee_name ? props.row.original.assignee_name : "";
                },
                size: 125,
            },
            {
                header: "Student Name",
                id: "student",
                accessorFn: (row) => {
                    const students = row.students || [];
                    if (students.length === 0) return "";
                    if (students.length === 1) return students[0].student_name || "";
                    return students.map(s => s.student_name).join(", ");
                },
                cell: (props) => {
                    const students = props.row.original.students || [];
                    if (students.length === 0) return "";
                    if (students.length === 1) {
                        return <span>{students[0].student_name || ""}</span>;
                    }
                    const displayNames = students.slice(0, 2).map(s => s.student_name).join(", ");
                    const remainingCount = students.length - 2;
                    return (
                        <span className="multi-student-display" title={students.map(s => s.student_name).join(", ")}>
                            <i className="fas fa-users student-icon"></i>
                            <span className="student-names">
                                {remainingCount > 0 ? `${displayNames} +${remainingCount} more` : displayNames}
                            </span>
                            <span className="student-count-badge">{students.length}</span>
                        </span>
                    );
                },
                size: 175,
            }
        );

        if (!this.props.isReporting) {
            // edit button only if regular (not reporting) view
            columns.push({
                header: () => null,
                accessorKey: "expand_plan",
                minSize: 50,
                maxSize: 50,
                enableColumnFilter: false,
                cell: (props) => {
                    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        event.preventDefault();
                        props.row.getToggleExpandedHandler()();
                    };

                    return (
                        <button className="btn btn-icon btn-toggle-expand" onClick={handleClick} aria-label="Expand or Compress">
                            <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} />
                        </button>
                    );
                },
            });
        } else {
            // add checkboxes if report choice view
            columns.unshift({
                header: () => (
                    <Button variant={"icon"} onClick={this.handleSelectAllToggle} disabled={this.props.tickets && this.props.tickets.length === 0}>
                        {this.props.ticketStore!.allSelected || this.props.tickets ? <i className="icon icon-check-square" /> : <i className="icon icon-square-o" />}
                    </Button>
                ),
                accessorKey: "isSelected",
                cell: (props) => (
                    <Button
                        variant={"icon"}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            if (this.props.selectTicket) {
                                this.props.selectTicket(props.row);
                            }
                            this.columns = this.getColumns();
                        }}
                    >
                        {this.isActionPlanSelected(props.row.original) ? <i className="icon icon-check-square" /> : <i className="icon icon-square-o" />}
                    </Button>
                ),
                minSize: 40,
                maxSize: 50,
                enableColumnFilter: false,
                enableSorting: false,
            });
        }

        columns.push({
            header: () => null,
            accessorKey: "actions",
            size: 50,
            enableColumnFilter: false,
            cell: (props) => {
                const rowId = props.row.id;
                const isOpen = this.state.openDropdownIndex === rowId;

                return (
                    <div className="d-flex align-items-center gap-1">
                        <TableMenu
                            key={props.row.original.display_id}
                            dashboardId={this.state.dashboardId}
                            row={props.row} isOpen={isOpen}
                            toggleDropdown={this.toggleDropdown}
                            onDeletePlan={this.handleDeleteClick}
                            onDownloadPlan={this.savePDF}
                            onToggleStatus={this.handleToggleStatus}
                        />
                        {this.props.ticketStore!.fetchingPDF === props.row.original.ruuid ? <Spinner animation="border" size="sm" /> : null}
                    </div>
                );
            },
        });

        return columns;
    };

    getInitialExpandedRowIndex = (actionPlans?: Ticket[]) => {
        let idx;
        if (!this.props.ticketStore || !this.state.instaOpenId) return;
        if (this.state.instaOpenId) {
            // idx = _.findIndex(this.props.ticketStore.tickets, { id: this.props.instaOpenId })
            // the only reason for using this less readable version of findIndex is that TS doesn't understand PartialDeep
            // We are only checking for the deprecated (old hashed id) here while we support old links from notifications.
            const tickets = actionPlans ? actionPlans : this.props.ticketStore.tickets;
            idx = _.findIndex(tickets, (o) => {
                return o.ruuid === this.state.instaOpenId;
            });
        }
        return idx;
    };

    getInitialExpandedRow = (actionPlans?: Ticket[]) => {
        const idx = this.getInitialExpandedRowIndex(actionPlans);
        if (idx !== undefined) {
            const visibleIdx = idx >= this.defaultPageSize ? idx % this.defaultPageSize : idx;
            const open: { [index: number]: boolean } = { [visibleIdx]: true };
            return open;
        } else {
            return undefined;
        }
    };

    getInitialPageNumber = (actionPlans?: Ticket[]) => {
        const idx = this.getInitialExpandedRowIndex(actionPlans);
        if (idx && idx >= this.defaultPageSize) {
            return Math.floor(idx / this.defaultPageSize);
        } else {
            return this.defaultPage;
        }
    };

    resetPage = () => {
        // try to be a little smart about it to avoid unnecessary diffing
        if (this.state.pageNumber !== this.defaultPage) {
            this.setState({ pageNumber: this.defaultPage });

            // Also update URL parameters to ensure consistency
            const currentUrl = new URL(window.location.href);
            const urlParams = new URLSearchParams(currentUrl.search);
            if (urlParams.get("page") !== "1") {
                urlParams.set("page", "1");
                if (this.props.navigate) {
                    this.props.navigate({
                        pathname: currentUrl.pathname,
                        search: urlParams.toString(),
                    }, { replace: true });
                }
            }
        }
    };

    // Shared scroll position management utilities
    private saveScrollPosition = () => {
        const scrollPosition = {
            x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
        };
        sessionStorage.setItem("actionplan-scroll-position", JSON.stringify(scrollPosition));
        sessionStorage.setItem("actionplan-scroll-timestamp", Date.now().toString());
    };

    private restoreScrollPosition = () => {
        try {
            const savedPosition = sessionStorage.getItem("actionplan-scroll-position");
            const timestamp = sessionStorage.getItem("actionplan-scroll-timestamp");

            if (savedPosition && timestamp) {
                // Only restore if the position was saved recently (within last 30 seconds)
                const timeDiff = Date.now() - parseInt(timestamp);
                if (timeDiff < 30000) {
                    const position = JSON.parse(savedPosition);
                    window.scrollTo({
                        left: position.x,
                        top: position.y,
                        behavior: "auto"
                    });
                    return true;
                }
            }
        } catch (error) {
            console.warn("Failed to restore scroll position:", error);
        }
        return false;
    };

    private scrollToRowByIndex = (rowIndex: number, useOldSelectors = false) => {
        // Check conditions for when to scroll
        const isExternalNavigation = document.referrer && !document.referrer.includes(window.location.hostname);
        const isPageReload = !document.referrer || document.referrer === window.location.href;
        const hasInstaOpenId = !!this.props.instaOpenId;

        // Scroll if: external navigation, page reload with ActionPlan ID, or legacy instaOpenId
        const shouldScroll = isExternalNavigation || (isPageReload && hasInstaOpenId) || hasInstaOpenId;

        if (!shouldScroll) {
            return; // Don't scroll for regular internal navigation
        }

        // Wait longer for expanded content to render, then scroll to the expanded form
        setTimeout(() => {
            // First, try to find the expanded ActionPlan form content
            const expandedContentSelectors = [
                ".expanded + tr td", // The expanded row content
                '[data-testid="ticket-detail-form"]', // The detail form itself
                ".ticket-detail-form", // Alternative class name
                "tr.expanded ~ tr", // Next sibling row (expanded content)
            ];

            let expandedContent: Element | null = null;
            for (const selector of expandedContentSelectors) {
                expandedContent = document.querySelector(selector);
                if (expandedContent) {
                    console.log("Found expanded content with selector:", selector);
                    break;
                }
            }

            // If expanded content found, scroll to it; otherwise fall back to row
            if (expandedContent) {
                expandedContent.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // Show from the top of the expanded content
                    inline: "nearest"
                });
                return;
            }

            // Fallback: scroll to the table row itself
            const rowSelectors = useOldSelectors ? [
                `div.list-container tbody tr:nth-child(${rowIndex + 1})`,
                `table tbody tr:nth-child(${rowIndex + 1})`,
                `[data-testid="action-plan-row"]:nth-child(${rowIndex + 1})`,
                `tr[data-expanded="true"]`
            ] : [
                `[data-row-index="${rowIndex}"]`,
                `tbody tr:nth-child(${rowIndex + 1})`,
                "tr[data-expanded=\"true\"]",
                `[role="row"]:nth-child(${rowIndex + 1})`
            ];

            let targetRow: Element | null = null;
            for (const selector of rowSelectors) {
                targetRow = document.querySelector(selector);
                if (targetRow) {
                    console.log("Found row with selector:", selector);
                    break;
                }
            }

            if (targetRow) {
                targetRow.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // Show from the top instead of nearest
                    inline: "nearest"
                });
            }
        }, useOldSelectors ? 300 : 400); // Increased delay for expanded content to render
    };

    handleScrollTo = (shouldScroll = false) => {
        // TODO: DEPRECATED - This method is being phased out in favor of the new URL parameter-based approach
        // Consider migrating to the ActionPlanReactTable's scrollToExpandedRow method
        console.warn("handleScrollTo is deprecated. Consider using URL parameter-based navigation.");

        // Only scroll if explicitly requested and there's an ActionPlan to scroll to
        if (!shouldScroll || !this.props.instaOpenId) return;

        // Try scroll restoration first
        if (this.restoreScrollPosition()) {
            return;
        }

        const idx = this.getInitialExpandedRowIndex();
        if (idx === undefined) return;

        // Calculate the visible row index within the current page
        const visibleIdx = idx >= this.defaultPageSize ? idx % this.defaultPageSize : idx;

        // Use shared scroll utility with legacy selectors
        this.scrollToRowByIndex(visibleIdx, true);
    };

    pushHistoryOnExpand = (ticketId?: string) => {
        this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name, ticketId));
    };

    listName = () => {
        const filters = this.state.showFilters ? " show-filters" : "";
        const fixed = this.props.dashboard || (this.props.isReporting && this.props.tickets) ? " not-fixed" : "";
        const reporting = this.props.isReporting ? " reporting" : "";
        const selection = this.props.isReporting && this.props.tickets ? " selection" : "";
        // reporting is fine without this part

        return `list-container${filters}${fixed}${reporting}${selection}`;
    };

    savePDF = (ticketId: string) => {
        this.props.ticketStore!.getTicketPDF(ticketId);
    };

    deleteActionplan = (id: string) => {
        const resetDeleteState = () => {
            this.setState({ deletingActionPlan: undefined });
        };
        this.setState({ deletingActionPlan: id, expand: undefined }, () => {
            this.props.ticketStore!.deleteTicket(id).then(() => {
                this.handleActionplanReload();
                resetDeleteState();
            });
        });
    };

    handleDeleteClick = (ticket: Ticket) => {
        this.props.appStore!.showConfirmModal({
            element: <span>{`Are you sure you want to delete ActionPlan ${ticket.display_id}?`}</span>,
            accept: () => this.deleteActionplan(ticket.ruuid),
            showActions: true,
        });
    };

    handleToggleStatus = (ticket: Ticket) => {
        if (ticket.type !== "task") return;

        if (ticket.status === "open") {
            this.checkMilestonesAndProceed(ticket);
        } else {
            this.props.appStore!.showConfirmModal({
                element: <div>Are you sure you want to re-open this ActionPlan?</div>,
                accept: () => {
                    this.reopenActionPlan(ticket);
                },
                cancel: () => { },
                showActions: true,
            });
        }
    };

    checkMilestonesAndProceed = async (ticket: Ticket) => {
        try {
            if (this.props.milestoneStore) {
                await this.props.milestoneStore.getAllMilestonesForActionPlan(ticket.ruuid);

                const hasIncompleteMilestones = this.props.milestoneStore.milestones.some(
                    milestone => milestone.status !== MilestoneStatus.COMPLETE
                );

                if (hasIncompleteMilestones) {
                    this.props.appStore!.showConfirmModal({
                        element: <p>This ActionPlan has milestones that have not been completed. Do you want to continue or do you want to cancel closing the ActionPlan for now?</p>,
                        accept: () => {
                            this.proceedWithClose(ticket);
                        },
                        cancel: () => { },
                        showActions: true,
                    });
                } else {
                    this.proceedWithClose(ticket);
                }
            } else {
                this.proceedWithClose(ticket);
            }
        } catch (error) {
            console.error("Error checking milestones:", error);
            this.proceedWithClose(ticket);
        }
    };

    proceedWithClose = (ticket: Ticket) => {
        if (ticket.dashboard_id) {
            this.setState({
                currentClosingTicket: ticket,
                showSnapshotConfirm: true,
            });
        } else {
            this.setState({
                currentClosingTicket: ticket,
                showCloseModal: true,
            });
        }
    };

    handleCloseWithSnapshot = () => {
        const ticket = this.state.currentClosingTicket!;
        this.setState({
            showSnapshotConfirm: false,
            currentClosingTicket: undefined,
        });

        if (this.props.navigate && ticket.dashboard_id) {
            const dashboardUrl = Utils.getActionplanUrl(ticket.dashboard, ticket.ruuid);
            this.props.navigate(`${dashboardUrl}?closeActionPlan=${ticket.ruuid}`);
        }
    };

    handleCloseWithoutSnapshot = () => {
        this.setState({
            showSnapshotConfirm: false,
            showCloseModal: true,
        });
    };

    removeCloseActionPlanQueryParam = () => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("closeActionPlan")) {
            params.delete("closeActionPlan");
            if (this.props.navigate) {
                this.props.navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
            }
        }
    };

    handleCancelClose = () => {
        this.setState({
            showSnapshotConfirm: false,
            showCloseModal: false,
            currentClosingTicket: undefined,
            closeError: undefined,
        });

        this.removeCloseActionPlanQueryParam();
    };

    handleSaveClose = async (finalComment: string, goalMet: boolean, attachmentId?: string) => {
        const ticket = this.state.currentClosingTicket!;
        this.setState({ isClosingActionPlan: true, closeError: undefined });

        try {
            const actionplanData: any = {
                ruuid: ticket.ruuid,
                status: "closed",
                target_met: goalMet,
                dashboard_id: ticket.dashboard_id,
            };

            const commentData: any = {
                comment: finalComment,
                created_by: Utils.getCurrentUser()?.name || undefined,
                attachments: [],
            };

            let skipSnapshotGeneration = false;
            let closingAttachmentId: string | undefined = undefined;

            if (this.props.dashboard !== undefined) {
                const { url, attachmentFile } = await Utils.getSnapshotAttachmentFileAndUrl(this.props.snapshotStore!, true);

                if (attachmentFile !== undefined) {
                    const snapshotAttachmentId = (await this.props.attachmentStore!.createAndUploadAttachments([attachmentFile]))[0];

                    // Let API know that snapshot has already been generated so it can skip generation using the custom view url
                    skipSnapshotGeneration = true;

                    commentData.attachments.push(snapshotAttachmentId);
                    closingAttachmentId = snapshotAttachmentId;
                }

                actionplanData.closed_dashboard_image_url = url;
                commentData.dashboard_image_url = url;
            }

            if (attachmentId) {
                commentData.attachments.push(attachmentId);
                // If user has selected an image to use in place of the dashboard snapshot, overwrite closingAttachmentId with their image's attachment id
                closingAttachmentId = attachmentId;
            }

            actionplanData.closed_attachment_id = closingAttachmentId;
            commentData.closed_attachment_id = closingAttachmentId;

            await this.props.ticketStore!.createOrUpdateTicket(actionplanData, Utils.getChosenSchoolInfo());

            if (this.props.commentStore) {
                this.props.snapshotStore!.resetSnapshot();
                await this.props.ticketStore!.addCommentToTicket(ticket.ruuid, commentData, skipSnapshotGeneration);
            }

            // Trigger immediate SWR refresh for real-time updates
            if (this.tableRef.current) {
                this.tableRef.current.mutateActionPlans();
            }

            this.handleActionplanReload();

            this.setState({
                showCloseModal: false,
                currentClosingTicket: undefined,
                isClosingActionPlan: false,
            });

            this.removeCloseActionPlanQueryParam();

        } catch (error) {
            console.error("Error closing ActionPlan:", error);
            this.setState({
                closeError: "Failed to close ActionPlan. Please try again.",
                isClosingActionPlan: false,
            });
        }
    };

    reopenActionPlan = async (ticket: Ticket) => {
        try {
            const actionplanData = {
                ruuid: ticket.ruuid,
                status: "open",
                dashboard_id: ticket.dashboard_id,
                closed_dashboard_image_url: "",
                snapshot_image_url: null,
            };

            await this.props.ticketStore!.createOrUpdateTicket(actionplanData, Utils.getChosenSchoolInfo());

            // Trigger immediate SWR refresh for real-time updates
            if (this.tableRef.current) {
                this.tableRef.current.mutateActionPlans();
            }

            this.handleActionplanReload();
        } catch (error) {
            console.error("Error reopening ActionPlan:", error);
            this.props.appStore!.showConfirmModal({
                element: <div>Failed to re-open ActionPlan. Please try again.</div>,
                accept: () => { },
                showActions: true,
            });
        }
    };

    handleClearSearch = () => {
        this.props.selectAllTickets ? this.props.selectAllTickets(false, this.props.tickets || !this.props.ticketStore ? [] : this.props.ticketStore.tickets) : null;
    };

    initTableau = async () => {
        if (this.viz) {
            this.viz.remove();
            this.viz = undefined;
        }

        if (this.props.trustedTokenStore) {
            await this.props.trustedTokenStore.getTrustedToken();
        }

        const chosenSchool = Utils.getChosenSchool();
        if (!chosenSchool) return;
        const vizUrl = CONFIG.paths.actionPlanDashboard.replace(":schoolId", chosenSchool);

        this.viz = new TableauViz();
        this.viz.src = vizUrl;
        this.viz.hideTabs = true;
        this.viz.toolbar = Toolbar.Hidden;
        this.viz.device = DeviceType.Desktop;
        this.viz.addEventListener(TableauEventType.FirstInteractive, async () => {
            await this.handleVizLoaded();
        });
    };

    handleToggleView = () => {
        const newView = this.state.view === VIEW_OPTIONS.DEFAULT ? VIEW_OPTIONS.STRATEGIC : VIEW_OPTIONS.DEFAULT;
        this.setState({ view: newView, sortOptions: newView === VIEW_OPTIONS.DEFAULT ? [] : [{ id: "strategic_plan_name", desc: false }] }, () => {
            this.handleActionplanReload();
            this.columns = this.getColumns();

            const params = this.getParams();
            if (newView === VIEW_OPTIONS.STRATEGIC) {
                params.set("view", "sp");
                this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name, this.state.instaOpenId), params);
            } else {
                params.delete("view");
                this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name, this.state.instaOpenId), params);
            }
        });
    };

    handleGenerateReport = () => {
        this.props.generateReport ? this.props.generateReport() : null;
    };

    handleCreateReportClick = () => { };

    handleShowQuickActionPlan = () => {
        this.setState({ showQuickActionPlan: true }, () => {
            if (this.quickActionPlanRef !== undefined) {
                this.quickActionPlanRef.scrollIntoView();
            }
        });
    };

    getQuickActionPlanRef = (ref) => {
        this.quickActionPlanRef = ref;
    };

    getPageRangeElement = (from: any, to: any, max: any) => (
        <div className="range">
            Showing{" "}
            <span className="fw-bold">
                {from} - {to}
            </span>{" "}
            out of <span className="fw-bold">{max}</span>
        </div>
    );

    shouldShowD3 = () => {
        return !this.props.dashboard && !this.props.isReporting;
    };

    renderPageRange = () => {
        // Get current pagination values from URL parameters or state
        const params = new URLSearchParams(window.location.search);
        const urlPageNumber = params.get("page") ? parseInt(params.get("page")!) - 1 : this.state.pageNumber;
        const urlPageSize = params.get("page_size") ? parseInt(params.get("page_size")!) : this.state.pageSize;

        const currentPageMin = urlPageSize * (urlPageNumber + 1) - urlPageSize + 1;
        const currentPageMax = urlPageSize * (urlPageNumber + 1);

        let to: any;
        let max: any;

        if (this.props.isReporting) {
            if (!this.props.tickets && this.props.ticketStore!.tickets) {
                // Report search results actionplan list
                to = Math.min(currentPageMax, this.props.ticketStore!.tickets.length);
                max = this.props.ticketStore!.tickets.length;
            } else {
                // Report selected actionplans list
                // if there are no selected Actionplans, render nothing
                if (!this.props.reportStore!.selectedTickets) return null;
                to = Math.min(currentPageMax, this.props.reportStore!.selectedTickets.length);
                max = this.props.reportStore!.selectedTickets.length;
            }
        } else {
            // All ActionPlans list (/actionplans or /dashboard/some_id)
            to = Math.min(currentPageMax, this.state.numOfTix);
            max = this.state.numOfTix;
        }

        return this.getPageRangeElement(currentPageMin, to, max);
    };

    updateCurrentPage = (page: number) => {
        this.setState({ pageNumber: page });
    };

    updatePageSize = (pageSize: number) => {
        this.setState({ pageSize: pageSize });
    };

    renderDecisionsList = () => {
        if (!this.props.ticketStore || !this.props.ticketStore.tickets || !this.props.strategicPlanStore || !this.props.strategicPlanStore.strategicPlanItems) return;

        const className = this.props.dashboard ? "dashboard-list-action-bar" : "";

        const titleText = this.state.view === VIEW_OPTIONS.STRATEGIC ? "Institutional Effectiveness" : "";
        const viewText = this.state.view === VIEW_OPTIONS.STRATEGIC ? "All" : "Institutional Effectiveness";
        return (
            <React.Fragment>
                {!this.props.isReporting && !this.props.dashboard ? (
                    <Row className={`section-action-bar actionplans ${className}`}>
                        <Col md={12} lg={4} className="title">
                            <h3 id="actionPlansTitle">{titleText} ActionPlans</h3>
                        </Col>
                        <Col>
                            <div className="actions">
                                {!this.props.dashboard && (this.state.view === VIEW_OPTIONS.STRATEGIC || this.props.strategicPlanStore.strategicPlanItems.length > 0) ? (
                                    <Dropdown className="ap-show-more" show={this.state.showMore} onToggle={(isOpen) => this.setState({ showMore: isOpen })}>
                                        <Dropdown.Toggle data-test-id="ap-show-more-btn" id="showMore" variant="outline" className={`expnad-menu ${this.state.showMore ? "active" : ""}`}>
                                            <span className="d-flex align-items-center gap-2">
                                                <EllipsisVertical />
                                                More
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item className="d-flex align-items-center gap-2" onClick={this.handleToggleView}>
                                                <Eye />
                                                <span className="fw-normal">View {viewText} ActionPlans</span>
                                            </Dropdown.Item>
                                            {this.props.featureGatingStore && !this.props.dashboard ? (
                                                Utils.isFeatureActive("api", "data_export", this.props.featureGatingStore.featureGatingList) ? (
                                                    <Dropdown.Item className="d-flex align-items-center gap-2" onClick={() => this.exportAllActionPlans()}>
                                                        <Upload />
                                                        <span className="fw-normal">Export All ActionPlan</span>
                                                    </Dropdown.Item>
                                                ) : null
                                            ) : null}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ) : null}
                                {this.props.dashboard || !Utils.hasAdminAccess() ? null : (
                                    <NavLink id="report-create-link" to={`${Utils.getLinkBase()}/reports/create`} title="New Report" aria-label="New Report" onClick={this.handleCreateReportClick}>
                                        {" "}
                                        <Plus /> New Report
                                    </NavLink>
                                )}
                                {this.props.dashboard ? null : (
                                    <button id="create-actionplan" title="Create ActionPlan" aria-label="Create ActionPlan" onClick={this.handleShowQuickActionPlan}>
                                        <Plus />
                                        New ActionPlan
                                    </button>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : null}
                {this.shouldShowD3() ? <D3Dashboard overdue={this.props.overdue} /> : null}
                <div className={this.listName()}>
                    <div className="action-container">
                        {this.props.isReporting ? (
                            <div className="component-actions">
                                <Row>
                                    <Col xs={12} md={6}>
                                        <span>{this.props.tickets ? "selected for report" : "search results"}</span>
                                    </Col>
                                    {this.props.tickets ? (
                                        <Col>
                                            <div className="ticket-selector">
                                                <Button variant="secondary" size="lg" disabled={this.props.selectAllTickets === undefined || this.props.tickets.length === 0} onClick={this.handleClearSearch}>
                                                    Clear Selected Search Results
                                                </Button>
                                                <Button variant="secondary" size="lg" onClick={this.handleGenerateReport} disabled={this.props.tickets.length === 0}>
                                                    Generate Report
                                                </Button>
                                            </div>
                                        </Col>
                                    ) : null}
                                </Row>
                            </div>
                        ) : null}
                        {this.state.showQuickActionPlan ? (
                            <>
                                <TicketDetailForm formRef={this.getQuickActionPlanRef} isPreview={false} dashboard={undefined} onCancel={this.handleCancel} onSaveDone={this.handleSaveDone} />
                            </>
                        ) : null}
                        <div className="list-actions border-top border-bottom">
                            <Row className="actionplan-actions align-items-center">
                                <Col xs={12} md={6} className="d-flex justify-content-start">
                                    {this.renderPageRange()}
                                </Col>
                                <Col>
                                    <div className="d-flex justify-content-end gap-2">
                                        <SearchActionPlans
                                            urlParams={this.params}
                                            columns={this.filterableColumns}
                                            onSearch={(search, key) => {
                                                const table = this.tableRef.current?.getTableInstance();

                                                if (!search || search.length === 0) {
                                                    // Only clear the specific search column, preserve other filters
                                                    if (table?.getColumn(key)) {
                                                        table?.getColumn(key)?.setFilterValue(undefined);
                                                    }
                                                } else {
                                                    if (table?.getColumn(key)) {
                                                        table?.getColumn(key)?.setFilterValue(search);
                                                    }
                                                }
                                            }}
                                        />

                                        <FilterButton
                                            urlParams={this.params}
                                            onApplyFilter={(filters) =>
                                                Object.entries(filters).forEach(([key, value]) => {
                                                    const table = this.tableRef.current?.getTableInstance();

                                                    if (table?.getColumn(key) && value) {
                                                        table?.getColumn(key)?.setFilterValue(value);
                                                    }
                                                })
                                            }
                                            onResetFilter={() => {
                                                const table = this.tableRef.current?.getTableInstance();
                                                table?.resetColumnFilters();
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <DrillDownFilterIndicator
                                getParams={() => this.getParams()}
                                onClear={this.handleClearDrillDownFilter}
                            />
                        </div>
                    </div>
                    <ActionPlanReactTable
                        ref={this.tableRef}
                        columns={this.columns}
                        data={this.props.tickets}
                        view={this.state.view}
                        dashboard={this.props.dashboard}
                        isReporting={this.props.isReporting}
                        pageIndex={this.state.pageNumber}
                        pageSize={this.state.pageSize}
                        showFilters={this.state.showFilters}
                        numOfTickets={this.state.numOfTix}
                        refreshView={this.state.refreshKey}
                        autoOpenIndex={this.state.autoOpenIndex}
                        handleActionplanReload={this.handleActionplanReload}
                        handleFiltersChange={this.handleFiltersChange}
                        refreshSelectedActionPlan={this.refreshSelectedActionPlan}
                        handleCancel={this.handleCancel}
                        handleSaveDone={this.handleSaveDone}
                        handleCloseDone={this.handleCloseDone}
                        pushHistory={this.pushHistoryOnExpand}
                        updateCurrentPage={this.updateCurrentPage}
                        updatePageSize={this.updatePageSize}
                        searchFilters={this.props.searchFilters}
                    />
                </div>
            </React.Fragment>
        );
    };
    shouldFetchAll = () => {
        return this.state.initialLoadDone && !this.state.didFetchAll && this.props.ticketStore && this.props.ticketStore.tickets && this.props.ticketStore.tickets.length <= this.state.pageSize;
    };

    fetchAppropriateTickets = async (id?: number, isFirstLoadOnDashboard = false) => {
        if (this.props.ticketStore) {
            this.setState({ expand: {} });

            if (id || this.props.dashboardId) {
                const dashId = id || this.props.dashboardId;
                if (!dashId) return;

                if (this.shouldFetchAll()) {
                    await this.props.ticketStore.getTicketsForDashboard(dashId, undefined, undefined, isFirstLoadOnDashboard);
                    if (this._isMounted) this.setState({ didFetchAll: true });
                } else if (!this.state.didFetchAll) {
                    await this.props.ticketStore.getTicketsForDashboard(dashId, this.state.pageSize, this.props.instaOpenId ? this.getInitialPageNumber() : this.state.pageNumber, isFirstLoadOnDashboard);
                }
            } else {
                if (this.shouldFetchAll()) {
                    await this.props.ticketStore.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC);
                    if (this._isMounted) this.setState({ didFetchAll: true });
                } else if (!this.state.didFetchAll) {
                    await this.props.ticketStore.fetchTickets(this.state.view === VIEW_OPTIONS.STRATEGIC, this.state.pageSize, this.state.pageNumber);
                }
            }

            if (this.props.instaOpenId && this.props.ticketStore.tickets) {
                this.handleAutoOpen();
            }

            if (this.props.ticketStore.tickets) {
                if (this._isMounted) {
                    this.setState(
                        {
                            initialLoadDone: true,
                            numOfTix: this.props.ticketStore.tickets.length,
                        },
                        () => {
                            if (this.state.numOfTix <= this.state.pageSize && !this.state.didFetchAll) {
                                this.fetchAppropriateTickets(id || this.props.dashboardId);
                            }
                        }
                    );
                }
            }
        }
    };

    handleAutoOpen = () => {
        this.autoOpenIndex = this.getInitialExpandedRowIndex();
        if (this._isMounted) {
            this.setState(
                {
                    expand: this.getInitialExpandedRow(),
                    pageNumber: this.getInitialPageNumber(),
                    autoOpenIndex: this.autoOpenIndex ?? -1,
                },
                () => {
                    // Only scroll if this is the initial load with a specific ActionPlan ID
                    const shouldScroll = Boolean(this.props.instaOpenId && this.autoOpenIndex !== undefined && this.autoOpenIndex >= 0);
                    this.handleScrollTo(shouldScroll);
                }
            );
        }
    };
    exportAllActionPlans = () => {
        this.props.ticketStore?.exportAllActionPlans();
    };

    refreshSelectedActionPlan = (actionPlans: Ticket[]) => {
        this.autoOpenIndex = this.getInitialExpandedRowIndex();
        if (this._isMounted) {
            this.setState({
                expand: this.getInitialExpandedRow(),
                pageNumber: this.getInitialPageNumber(actionPlans),
                autoOpenIndex: this.autoOpenIndex ?? -1,
            });
        }
    };

    render() {
        return (
            <div ref={this.props.formRef}>
                {this.props.ticketStore && this.props.ticketStore.tickets && this.state.initialLoadDone ? this.renderDecisionsList() : null}
                {this.props.ticketStore && (!this.props.ticketStore.tickets || this.props.ticketStore.tickets.length !== 0) && !this.state.initialLoadDone ? (
                    <div style={{ textAlign: "center", paddingTop: 180 }}>
                        <Spinner animation="border" />
                    </div>
                ) : null}

                <SnapshotConfirmModal
                    show={this.state.showSnapshotConfirm}
                    onCancel={this.handleCancelClose}
                    onWithSnapshot={this.handleCloseWithSnapshot}
                    onWithoutSnapshot={this.handleCloseWithoutSnapshot}
                />

                <ActionplanCloseModal
                    show={this.state.showCloseModal}
                    onCancel={this.handleCancelClose}
                    onSave={this.handleSaveClose}
                    isQuickActionplan={!this.state.currentClosingTicket?.dashboard_id}
                    isSaving={this.state.isClosingActionPlan}
                />

                {this.state.closeError && (
                    <Modal show={true} onHide={() => this.setState({ closeError: undefined })}>
                        <Modal.Body>
                            <div className="alert alert-danger">
                                {this.state.closeError}
                            </div>
                            <div className="text-end">
                                <Button onClick={() => this.setState({ closeError: undefined })}>
                                    OK
                                </Button>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        );
    }

    handleSaveDone = async (ruuid?: string, isUpdate?: boolean) => {
        // Save scroll position before refresh
        this.saveScrollPosition();

        // Show success, refresh list, and expand the just-saved action plan
        if (this.context && this.context.toast) {
            this.context.toast({
                type: "success",
                title: "Success",
                message: isUpdate
                    ? "Action Plan updated successfully. Returning to the Action Plans list."
                    : "Action Plan created successfully. Returning to the Action Plans list.",
                duration: 5000,
            });
        }
        this.setState({
            showQuickActionPlan: false,
            showActionPlanSuccess: true,
            instaOpenId: ruuid || "",
            refreshKey: new Date().toISOString()
        }, async () => {
            // Trigger immediate SWR refresh for real-time updates
            if (this.tableRef.current) {
                this.tableRef.current.mutateActionPlans();
            }
            await this.fetchAppropriateTickets();
            this.handleAutoOpen();
        });
    };

    handleCloseDone = () => {
        // Save scroll position before any refresh
        this.saveScrollPosition();

        if (this.props.instaOpenId && this.props.ticketStore && this.props.ticketStore.tickets) {
            this.handleAutoOpen();
        }
    };

    handleCancel = () => {
        this.autoClose();
    };

    autoClose = (nextState?: any, callback?: any) => {
        // The pushed URL must not carry the ActionPlan id: keeping it re-triggers
        // the URL-parameter auto-open and reopens the plan the user just collapsed (EAI-5687).
        if (this.props.dashboardId) {
            const params = this.getParams();
            params.delete("viewId");
            params.delete("refreshView");
            this.pushHistory(Utils.getActionplanUrl(this.props.dashboard?.name), params);
        } else if (!this.props.isReporting) {
            this.pushHistory(Utils.getActionplanUrl());
        }

        this.setState({ instaOpenId: "", autoOpenIndex: -1, expand: {}, showQuickActionPlan: false, ...nextState }, callback || (() => { }));
    };
}

interface DrillDownFilterIndicatorProps {
    getParams: () => URLSearchParams;
    onClear: () => void;
}

const DrillDownFilterIndicator = ({ getParams, onClear }: DrillDownFilterIndicatorProps) => {
    const params = getParams();
    const planRuuid = params.get("strategic_plan_ruuid");
    const planName = params.get("strategic_plan_name");
    const status = params.get("status");

    if (!planRuuid) return null;

    const planLabel = planName ? decodeURIComponent(planName) : "Effectiveness Plan";
    const statusLabel = status ? getStatusLabelFromApi(status) : "";

    return (
        <div className="d-flex align-items-center gap-2 px-2 py-2 flex-wrap">
            <span className="fw-semibold small text-muted">Filtered by:</span>
            {statusLabel && (
                <Badge bg="light" text="dark" className="d-inline-flex align-items-center gap-1 border px-2 py-1 fw-normal">
                    Status: {statusLabel}
                    <X size={14} role="button" className="ms-1 cursor-pointer" onClick={onClear} aria-label="Clear drill-down filter" />
                </Badge>
            )}
            <Badge bg="light" text="dark" className="d-inline-flex align-items-center gap-1 border px-2 py-1 fw-normal">
                Plan: {planLabel}
                <X size={14} role="button" className="ms-1 cursor-pointer" onClick={onClear} aria-label="Clear drill-down filter" />
            </Badge>
        </div>
    );
};

interface ActionPlanReactTableProps {
    columns: ColumnDef<Ticket>[];
    data?: Ticket[];
    dashboard?: Dashboard;
    isReporting?: boolean;
    pageIndex: number;
    pageSize: number;
    showFilters: boolean;
    numOfTickets: number;
    autoOpenIndex: number;
    refreshView?: string | null;
    handleActionplanReload: () => void;
    handleFiltersChange: (rows: number) => void;
    refreshSelectedActionPlan: (actionPlans: Ticket[]) => void;
    handleCancel: () => void;
    handleSaveDone: (ruuid?: string, isUpdate?: boolean) => void;
    handleCloseDone: () => void;
    pushHistory: (ticketId?: string) => void;
    updateCurrentPage: (pg: number) => void;
    updatePageSize: (pgSize: number) => void;
    view: VIEW_OPTIONS;
    searchFilters?: ActionPlanSearchFilters;
}

export type ActionPlanTableHandle = {
    getTableInstance: () => ITable<Ticket>;
    mutateActionPlans: () => void;
};

const ActionPlanReactTable = forwardRef<ActionPlanTableHandle, ActionPlanReactTableProps>(({ columns, data, view, dashboard, isReporting, pageIndex, pageSize, numOfTickets, autoOpenIndex, refreshView, handleActionplanReload, handleFiltersChange, refreshSelectedActionPlan, handleCancel, handleSaveDone, handleCloseDone, pushHistory, updateCurrentPage, updatePageSize, searchFilters }, ref) => {
    const query = useQuery();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ actionplanId?: string; ticketId?: string }>();
    const targetActionPlanId = params.actionplanId ?? params.ticketId;

    // Add URL parameter reading for all filters
    const statusFilter = query.get("status");
    const createdStartFilter = query.get("created_start");
    const createdEndFilter = query.get("created_end");
    const evaluationStartFilter = query.get("evaluation_start");
    const evaluationEndFilter = query.get("evaluation_end");
    const queryFilter = query.get("query");
    const queryByFilter = query.get("key");
    const sortFilter = query.get("sort");
    const sortOrderFilter = query.get("order");
    const pageFilter = query.get("page");
    const pageSizeFilter = query.get("page_size");
    const studentFilter = query.get("student");
    const dashboardFilter = query.get("dashboard");
    const courseFilter = query.get("course");
    const committeeFilter = query.get("committees");
    const labelsFilter = query.get("labels");
    const strategicPlanRuuid = query.get("strategic_plan_ruuid");
    const initialTab = query.get("tab") ?? undefined;

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);

    // Use URL parameters for pagination state, with fallback to props
    const currentPageIndex = pageFilter ? parseInt(pageFilter) - 1 : pageIndex;
    const currentPageSize = pageSizeFilter ? parseInt(pageSizeFilter) : pageSize;
    const pagination = useMemo(() => ({
        pageIndex: currentPageIndex,
        pageSize: currentPageSize
    }), [currentPageIndex, currentPageSize]);

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [initialExpander, setInitialExpander] = useState(true);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [shouldRestoreScroll, setShouldRestoreScroll] = useState(true);
    // Tracks whether the user has actively clicked a pagination control in this session.
    // We can't derive this from currentPageIndex changes because the parent class component
    // mutates pageNumber during the initial mount (handleAutoOpen / handleFiltersChange),
    // which would false-positive the flag and disable the deep-link prepend.
    const [userHasPaginated, setUserHasPaginated] = useState(false);
    // Use searchFilters when in reporting mode with filters, otherwise use URL parameters
    const useSearchFilters = isReporting && searchFilters;
    const { data: actionplansData, mutate: mutateActionPlans } = useActionPlans({
        strategicPlanOnly: view === VIEW_OPTIONS.STRATEGIC,
        pageSize: currentPageSize,
        pageNumber: currentPageIndex,
        queryFilter: useSearchFilters ? searchFilters.queryFilter : queryFilter,
        queryByFilter: useSearchFilters ? "name" : queryByFilter, // Default to name search for keywords
        statusFilter: useSearchFilters ? searchFilters.statusFilter : statusFilter,
        sortFilter,
        sortOrderFilter,
        createdStartFilter: useSearchFilters ? searchFilters.createdStartFilter : createdStartFilter,
        createdEndFilter: useSearchFilters ? searchFilters.createdEndFilter : createdEndFilter,
        evaluationStartFilter: useSearchFilters ? searchFilters.evaluationStartFilter : evaluationStartFilter,
        evaluationEndFilter: useSearchFilters ? searchFilters.evaluationEndFilter : evaluationEndFilter,
        refreshKey: refreshView,
        dashboardId: dashboard ? dashboard.id : (useSearchFilters && searchFilters.dashboardFilter ? searchFilters.dashboardFilter : dashboardFilter ? parseInt(dashboardFilter) : undefined),
        studentFilter: useSearchFilters ? searchFilters.studentFilter : studentFilter,
        courseFilter: useSearchFilters ? searchFilters.courseFilter : courseFilter,
        committeeFilter: useSearchFilters ? searchFilters.committeeFilter : committeeFilter,
        labelsFilter: useSearchFilters ? searchFilters.labelsFilter : labelsFilter,
        assigneeFilter: useSearchFilters ? searchFilters.assigneeFilter : undefined,
        creatorFilter: useSearchFilters ? searchFilters.creatorFilter : undefined,
        strategicPlanFilter: useSearchFilters ? searchFilters.strategicPlanFilter : undefined,
        strategicPlanRuuid: useSearchFilters ? undefined : strategicPlanRuuid,
    }, !data);

    const isExpandState = (updater: Updater<ExpandedState>): updater is ExpandedState => {
        const castUpdater = updater as ExpandedState;

        return typeof castUpdater === "boolean" || typeof castUpdater === "object";
    };

    const baseTableData: Ticket[] = !data ? actionplansData?.data ?? [] : data;

    const targetInBaseData = targetActionPlanId
        ? baseTableData.some(t => t.ruuid === targetActionPlanId)
        : true;

    // Prepend is a landing-page courtesy. The user must always see the linked plan after
    // clicking the email, even when the URL carries an unrelated page param. Once the user
    // actively paginates, they're in browsing mode and the prepend bows out.
    const shouldPrependDeepLink = Boolean(targetActionPlanId) && !targetInBaseData && !userHasPaginated;

    const { data: deepLinkedActionPlan } = useActionPlanById(
        shouldPrependDeepLink ? targetActionPlanId : null
    );

    const tableData: Ticket[] = (shouldPrependDeepLink && deepLinkedActionPlan)
        ? [deepLinkedActionPlan, ...baseTableData]
        : baseTableData;

    // Calculate total pages based on actual data being displayed
    const currentData = tableData;
    let actualTotalCount;

    if (isReporting && data) {
        // For reporting mode with provided data, use the data length
        actualTotalCount = data.length;
    } else if (actionplansData?.result?.total_count) {
        // Use API total count if available (for paginated results)
        actualTotalCount = actionplansData.result.total_count;
    } else {
        // Fallback to parent component's numOfTickets or current data length
        actualTotalCount = numOfTickets ?? currentData.length;
    }

    const totalPages = Math.ceil(actualTotalCount / (pagination.pageSize || 10));

    const setExpandHandler = (updater: Updater<ExpandedState>) => {
        if (isExpandState(updater)) {
            setExpanded(updater);
        } else {
            const news = updater(expanded);
            const oldKeys = Object.keys(expanded);
            const newKeys = Object.keys(news);
            const filteredKeys = newKeys.filter((key) => !oldKeys.includes(key));
            const newExpanded: Record<string, boolean> = {};
            filteredKeys.forEach((filtered) => (newExpanded[filtered] = true));
            if (!isReporting) {
                if (filteredKeys.length > 0) {
                    const idx = Number(filteredKeys[0]);
                    const ticket = tableData[idx];
                    if (ticket) {
                        pushHistory(ticket.ruuid);
                    }
                } else {
                    pushHistory();
                    // User-initiated collapse: clear parent's auto-open state so the
                    // deep-linked plan doesn't pop back open on the next render.
                    handleCancel();
                }
            }
            setExpanded(newExpanded);
        }
    };

    const table = useReactTable({
        columns,
        data: tableData,
        state: {
            columnFilters,
            sorting,
            pagination,
            expanded,
        },
        getCoreRowModel: getCoreRowModel(),
        getRowCanExpand: () => true,
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        onExpandedChange: setExpandHandler,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
    });

    useImperativeHandle(ref, () => ({
        getTableInstance: () => table,
        mutateActionPlans: () => mutateActionPlans(),
    }));

    // Scroll position management utilities
    const saveScrollPosition = () => {
        const scrollPosition = {
            x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
        };
        sessionStorage.setItem("actionplan-scroll-position", JSON.stringify(scrollPosition));
        sessionStorage.setItem("actionplan-scroll-timestamp", Date.now().toString());
    };

    const restoreScrollPosition = () => {
        try {
            const savedPosition = sessionStorage.getItem("actionplan-scroll-position");
            const timestamp = sessionStorage.getItem("actionplan-scroll-timestamp");

            if (savedPosition && timestamp) {
                // Only restore if the position was saved recently (within last 30 seconds)
                const timeDiff = Date.now() - parseInt(timestamp);
                if (timeDiff < 30000) {
                    const position = JSON.parse(savedPosition);
                    setTimeout(() => {
                        window.scrollTo({
                            left: position.x,
                            top: position.y,
                            behavior: "auto" // Use instant scroll for restoration
                        });
                    }, 100);
                    return true;
                }
            }
        } catch (error) {
            console.warn("Failed to restore scroll position:", error);
        }
        return false;
    };

    const clearScrollPosition = () => {
        sessionStorage.removeItem("actionplan-scroll-position");
        sessionStorage.removeItem("actionplan-scroll-timestamp");
    };

    // Helper function to scroll to expanded row (only for external navigation)
    const scrollToExpandedRow = (rowIndex: number) => {
        // Don't scroll if we should restore the previous position instead
        if (shouldRestoreScroll) {
            const restored = restoreScrollPosition();
            if (restored) {
                setShouldRestoreScroll(false);
                return;
            }
        }

        // Only scroll to ActionPlan if coming from external link (like email)
        // Check if this is likely from an external navigation
        const isExternalNavigation = document.referrer && !document.referrer.includes(window.location.hostname);
        const hasActionPlanInURL = targetActionPlanId;

        if (!isExternalNavigation && !hasActionPlanInURL) {
            return; // Don't scroll for internal navigation
        }

        // Wait for expanded content to render, then scroll to it
        setTimeout(() => {
            // First priority: Find the expanded ActionPlan form content
            const expandedContentSelectors = [
                ".expanded + tr td", // The expanded row content
                '[data-testid="ticket-detail-form"]', // The detail form itself
                ".ticket-detail-form", // Alternative class name
                "tr.expanded ~ tr", // Next sibling row (expanded content)
            ];

            let expandedContent: Element | null = null;
            for (const selector of expandedContentSelectors) {
                expandedContent = document.querySelector(selector);
                if (expandedContent) {
                    console.log("Found expanded content with selector:", selector);
                    break;
                }
            }

            // If expanded content found, scroll to it
            if (expandedContent) {
                expandedContent.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // Show from the top of the expanded content
                    inline: "nearest"
                });
                return;
            }

            // Fallback: scroll to the table row itself
            const rowSelectors = [
                `[data-row-index="${rowIndex}"]`,
                `tbody tr:nth-child(${rowIndex + 1})`,
                "tr[data-expanded=\"true\"]",
                `[role="row"]:nth-child(${rowIndex + 1})`
            ];

            let targetRow: Element | null = null;
            for (const selector of rowSelectors) {
                targetRow = document.querySelector(selector);
                if (targetRow) {
                    console.log("Found row with selector:", selector);
                    break;
                }
            }

            if (targetRow) {
                targetRow.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // Show from the top instead of nearest
                    inline: "nearest"
                });
            }
        }, 400); // Increased delay for expanded content to render
    };

    // Handle scroll position restoration on component mount
    useEffect(() => {
        let scrollSaveTimeout: NodeJS.Timeout;

        // Try to restore scroll position on initial load
        if (shouldRestoreScroll && !targetActionPlanId) {
            const restored = restoreScrollPosition();
            if (restored) {
                setShouldRestoreScroll(false);
            }
        }

        // Save scroll position before page unload
        const handleBeforeUnload = () => {
            saveScrollPosition();
        };

        // Save scroll position on various actions
        const handleScroll = () => {
            // Throttle scroll position saving
            clearTimeout(scrollSaveTimeout);
            scrollSaveTimeout = setTimeout(() => {
                saveScrollPosition();
            }, 100);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("scroll", handleScroll);
            clearTimeout(scrollSaveTimeout);
        };
    }, [shouldRestoreScroll, targetActionPlanId]);

    // Synchronize parent component state with URL parameters
    useEffect(() => {
        const urlPageIndex = pageFilter ? parseInt(pageFilter) - 1 : 0;
        const urlPageSize = pageSizeFilter ? parseInt(pageSizeFilter) : pageSize;

        // Always update parent component state to match URL parameters
        updateCurrentPage(urlPageIndex);
        updatePageSize(urlPageSize);
    }, [pageFilter, pageSizeFilter, updateCurrentPage, updatePageSize, pageSize]);

    // Update parent component when total count changes
    useEffect(() => {
        if (actualTotalCount !== undefined && handleFiltersChange) {
            handleFiltersChange(actualTotalCount);
        }
    }, [actualTotalCount, handleFiltersChange]);

    // Rearm auto-open only when navigating TO a specific ActionPlan. When the id
    // is cleared (the user collapsed the row), rearming would let the auto-open
    // effect immediately re-expand the row on the next data refresh (EAI-5687).
    useEffect(() => {
        if (targetActionPlanId) {
            setHasAutoOpened(false);
            // Clear old scroll position when navigating to a specific ActionPlan
            clearScrollPosition();
            setShouldRestoreScroll(false);
        }
    }, [targetActionPlanId]);

    // Quick-create expands the new plan via autoOpenIndex without a URL change,
    // so a fresh index must rearm auto-open itself.
    useEffect(() => {
        if (autoOpenIndex >= 0) {
            setHasAutoOpened(false);
        }
    }, [autoOpenIndex]);

    useEffect(() => {
        // targetActionPlanId covers both /actionplans/:actionplanId and
        // /dashboard/:dashboardName/:ticketId (email notification link shapes).
        // Re-sync on every tableData change so pagination keeps the target expanded when
        // it's visible on the new page, and clears stale expansion when it isn't.
        if (targetActionPlanId && tableData.length > 0) {
            const targetIndex = tableData.findIndex(ticket => ticket.ruuid === targetActionPlanId);
            if (targetIndex >= 0) {
                const key = targetIndex.toString();
                const newExpanded: Record<string, boolean> = { [key]: true };
                setExpanded(newExpanded);
                if (!hasAutoOpened) {
                    scrollToExpandedRow(targetIndex);
                    setHasAutoOpened(true);
                }
            } else {
                setExpanded({});
            }
        } else if (autoOpenIndex >= 0 && tableData.length > 0 && !targetActionPlanId && !hasAutoOpened) {
            const key = autoOpenIndex.toString();
            if (autoOpenIndex < tableData.length) {
                const newExpanded: Record<string, boolean> = { [key]: true };
                setExpanded(newExpanded);
                setHasAutoOpened(true);
            }
        }
    }, [autoOpenIndex, targetActionPlanId, tableData, hasAutoOpened]);

    useEffect(() => {
        const length = Object.keys(expanded).length;
        if (length > 0) {
            // Save scroll position when expanding
            saveScrollPosition();

            // Only call refreshSelectedActionPlan for auto-opening from URL parameters
            // Don't call it for manual row expansions to prevent pagination reset
            if (autoOpenIndex >= 0) {
                const filteredActionPlans = table.getFilteredRowModel().rows.map((row) => row.original);
                refreshSelectedActionPlan(filteredActionPlans);
            }
        } else if (initialExpander) {
            setInitialExpander(false);
        }
        // User-initiated collapse already strips the URL and resets parent state
        // via setExpandHandler; firing handleCancel here would also strip the URL on
        // programmatic collapses (e.g. when SWR refetches and the deep-linked target
        // isn't on the new page), breaking the email deep-link landing flow.
    }, [expanded]);

    useEffect(() => {
        const filteredRows = actionplansData?.result?.total_count || 0;

        if (filteredRows !== numOfTickets) {
            handleFiltersChange(filteredRows);
        }
    }, [actionplansData]);

    const getSortName = (id: string): string => {
        const sortMap = {
            created: "enflux_created",
            dashboard: "dashboard_name",
            name: "insight",
            created_by_name: "created_by",
        };
        return sortMap[id] || id;
    };

    // sorting
    useEffect(() => {
        // Save scroll position before sorting
        saveScrollPosition();

        if (sorting.length > 0) {
            query.set("sort", getSortName(sorting[0].id));
            query.set("order", sorting[0].desc ? "desc" : "asc");
            navigate({
                pathname: location.pathname,
                search: query.toString(),
            });
        } else {
            query.delete("sort");
            query.delete("order");
            navigate({
                pathname: location.pathname,
                search: query.toString(),
            });
        }
    }, [sorting]);

    const getCancelHandler = (row: RowInfo<Ticket>) => {
        const handler = row.getToggleExpandedHandler();
        return () => {
            handler();
            handleCancel();
        };
    };

    // Saving keeps the row expanded; collapsing stays a user action. The saved
    // plan's ruuid is deliberately not forwarded — setting instaOpenId for an
    // already-expanded row re-triggers the auto-open machinery (EAI-5687).
    const getSaveDoneHandler = () => {
        return (_ruuid?: string, isUpdate?: boolean) => {
            handleSaveDone(undefined, isUpdate);
        };
    };

    const onClickRowHandler = (row: RowInfo<Ticket>, event: MouseEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        event.stopPropagation();
        row.getToggleExpandedHandler()();
    };

    const renderSubComponent = (row: RowInfo<Ticket>) => {
        const isDeepLinkedRow = !!targetActionPlanId && row.original.ruuid === targetActionPlanId;
        return <TicketDetailForm dashboard={dashboard} currentTicket={row.original} refreshView={refreshView} isPreview={isReporting || false} onUpdate={handleActionplanReload} onCancel={getCancelHandler(row)} onSaveDone={getSaveDoneHandler()} onCloseDone={handleCloseDone} initialTab={isDeepLinkedRow ? initialTab : undefined} />;
    };

    return (
        <div className="list">
            <Table>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id} id={header.id}>
                                    {header.isPlaceholder ? null : (
                                        <>
                                            <div className={header.column.getCanSort() ? "d-flex align-items-center gap-2 cursor-pointer " + (header.column.getIsSorted() || "") : ""} onClick={header.column.getToggleSortingHandler()}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}

                                                {header.column.getIsSorted() === "asc" ? <ArrowUp /> : header.column.getIsSorted() === "desc" ? <ArrowDown /> : <ArrowDownUp />}
                                            </div>
                                        </>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <Fragment key={row.id}>
                            <tr key={row.id} onClick={(event) => onClickRowHandler(row, event)} className={row.getIsExpanded() ? "expanded" : "expandable"}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                            {row.getIsExpanded() && (
                                <tr>
                                    <td colSpan={row.getVisibleCells().length}>{renderSubComponent(row)}</td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                No Results
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </Table>
            <PagePagination
                table={table}
                totalPages={totalPages}
                page={pagination.pageIndex}
                pageSize={pagination.pageSize}
                setCurrentPage={page => {
                    setUserHasPaginated(true);
                    // Update URL parameters
                    const newQuery = new URLSearchParams(query);
                    newQuery.set("page", (page + 1).toString());

                    navigate({
                        pathname: location.pathname,
                        search: newQuery.toString(),
                    }, { replace: true });

                    // Update parent component state
                    updateCurrentPage(page);
                }}
                setPageSize={newPageSize => {
                    setUserHasPaginated(true);
                    // Reset to first page when changing page size
                    const newQuery = new URLSearchParams(query);
                    newQuery.set("page_size", newPageSize.toString());
                    newQuery.set("page", "1");

                    navigate({
                        pathname: location.pathname,
                        search: newQuery.toString(),
                    }, { replace: true });

                    // Update parent component state
                    updatePageSize(newPageSize);
                    updateCurrentPage(0);
                }} />
        </div>
    );
});

ActionPlanReactTable.displayName = "ActionPlanReactTable";
